import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, IconButton, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography } from '@mui/material';
import { useNotify, useTranslate } from 'react-admin';
import { EmbedToken, embedTokenApi, StorefrontAdminProduct, storefrontAdminApi } from '../../../services/storefrontService';
import MaterialSymbol from '../../common/MaterialSymbol';
import EmbedSnippetDialog from './EmbedSnippetDialog';
import EmbedTokenFormDialog from './EmbedTokenFormDialog';

/** Tab „Einbetten“: Widgets (Embed-Tokens) der Storefront verwalten. */
const EmbedTokensPanel: React.FC = () => {
    const translate = useTranslate();
    const notify = useNotify();
    const [tokens, setTokens] = useState<EmbedToken[] | null>(null);
    const [products, setProducts] = useState<StorefrontAdminProduct[]>([]);
    const [storefrontReady, setStorefrontReady] = useState(true);
    const [error, setError] = useState(false);
    const [form, setForm] = useState<{ embed: EmbedToken | null } | null>(null);
    const [snippet, setSnippet] = useState<EmbedToken | null>(null);
    const [revoking, setRevoking] = useState<EmbedToken | null>(null);

    const load = useCallback(() => {
        embedTokenApi.list().then(setTokens).catch(() => setError(true));
        storefrontAdminApi.getConfig().then((r) => setStorefrontReady(r.config !== null && r.config.enabled)).catch(() => setStorefrontReady(true));
        storefrontAdminApi.products().then(setProducts).catch(() => setProducts([]));
    }, []);
    useEffect(load, [load]);

    if (error) return <Alert severity="error">{translate('embed.loadError')}</Alert>;
    if (!tokens) return <CircularProgress />;

    const confirmRevoke = async () => {
        if (!revoking) return;
        try {
            const updated = await embedTokenApi.revoke(revoking.id);
            setTokens((list) => (list ?? []).map((t) => (t.id === updated.id ? updated : t)));
            notify(translate('embed.revoke.done'), { type: 'success' });
        } catch {
            notify(translate('embed.error'), { type: 'error' });
        }
        setRevoking(null);
    };

    const onSaved = (saved: EmbedToken, created: boolean) => {
        setForm(null);
        setTokens((list) => (created ? [saved, ...(list ?? [])] : (list ?? []).map((t) => (t.id === saved.id ? saved : t))));
        if (created) setSnippet(saved);
    };

    return (
        <Box>
            <Typography color="text.secondary" sx={{ mb: 2 }}>{translate('embed.intro')}</Typography>
            {!storefrontReady ? <Alert severity="info" sx={{ mb: 2 }}>{translate('embed.needsStorefront')}</Alert> : null}
            <Button variant="contained" startIcon={<MaterialSymbol name="add" />} onClick={() => setForm({ embed: null })} disabled={!storefrontReady} sx={{ mb: 2 }}>
                {translate('embed.create')}
            </Button>

            {tokens.length === 0 ? (
                <Typography>{translate('embed.empty')}</Typography>
            ) : (
                <Box sx={{ overflowX: 'auto' }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>{translate('embed.columns.label')}</TableCell>
                                <TableCell>{translate('embed.columns.type')}</TableCell>
                                <TableCell>{translate('embed.columns.origins')}</TableCell>
                                <TableCell>{translate('embed.columns.status')}</TableCell>
                                <TableCell>{translate('embed.columns.lastUsed')}</TableCell>
                                <TableCell align="right">{translate('embed.columns.actions')}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {tokens.map((t) => {
                                const active = t.status === 'active';
                                return (
                                    <TableRow key={t.id} data-testid={`embed-token-${t.id}`}>
                                        <TableCell>{t.label}<Typography variant="caption" component="div" color="text.secondary">{t.tokenPreview}</Typography></TableCell>
                                        <TableCell>{translate(`embed.types.${t.widgetType}.label`)}</TableCell>
                                        <TableCell sx={{ maxWidth: 280, wordBreak: 'break-all' }}>
                                            {t.allowedOrigins.length === 0
                                                ? <Typography variant="body2" color="warning.main">{translate('embed.noOrigins')}</Typography>
                                                : t.allowedOrigins.join(', ')}
                                        </TableCell>
                                        <TableCell><Chip size="small" variant="outlined" label={translate(`embed.status.${t.status}`)} /></TableCell>
                                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{t.lastUsedAt ? new Date(t.lastUsedAt).toLocaleString() : translate('embed.never')}</TableCell>
                                        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                            <Tooltip title={translate('embed.actions.snippet')}>
                                                <span>
                                                    <IconButton size="small" aria-label={`${translate('embed.actions.snippet')}: ${t.label}`} disabled={!active} onClick={() => setSnippet(t)}>
                                                        <MaterialSymbol name="code" />
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                            <Tooltip title={translate('embed.actions.edit')}>
                                                <span>
                                                    <IconButton size="small" aria-label={`${translate('embed.actions.edit')}: ${t.label}`} disabled={!active} onClick={() => setForm({ embed: t })}>
                                                        <MaterialSymbol name="edit" />
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                            <Tooltip title={translate('embed.actions.revoke')}>
                                                <span>
                                                    <IconButton size="small" aria-label={`${translate('embed.actions.revoke')}: ${t.label}`} disabled={!active} onClick={() => setRevoking(t)}>
                                                        <MaterialSymbol name="cancel" />
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </Box>
            )}

            <EmbedTokenFormDialog open={form !== null} embed={form?.embed ?? null} products={products} onClose={() => setForm(null)} onSaved={onSaved} />
            <EmbedSnippetDialog embed={snippet} onClose={() => setSnippet(null)} />
            <Dialog open={revoking !== null} onClose={() => setRevoking(null)} aria-labelledby="embed-revoke-title">
                <DialogTitle id="embed-revoke-title">{translate('embed.revoke.title')}</DialogTitle>
                <DialogContent>
                    <DialogContentText>{translate('embed.revoke.text', { label: revoking?.label ?? '' })}</DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRevoking(null)}>{translate('embed.form.cancel')}</Button>
                    <Button color="error" variant="contained" onClick={confirmRevoke}>{translate('embed.revoke.confirm')}</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default EmbedTokensPanel;
